// Клиент `/api/v1/agent_tool_blacklist`.
//
// Управление per-(org, role) blacklist'ом инструментов из UI «Настройки →
// Запрещённые инструменты». Зеркалит pydantic-схемы backend'а
// (`ToolListEntryResponse` / `AddToolRequest`). JWT прикручивает
// `authedFetch`; org берётся из токена (RLS), не из тела.
//
// Список отдаётся голым массивом, без обёртки `{items}`. Запись
// адресуется парой `(role_name, tool_name)`: `DELETE` несёт оба в query,
// без параметрического пути. `POST` отвечает 200 и на создание, и на
// повтор; `DELETE` — 204 без тела и идемпотентно (не 404, если записи уже
// не было; 404 — только незарегистрированная роль).

import { authedFetch } from "../../auth/authedFetch";

// Источник запрета: `manual` (правка человеком) или `self_improvement`
// (применение предложения `tool_blacklist`). Держим типом для Badge.
export type ToolBlacklistSource = "manual" | "self_improvement";

export interface ToolBlacklistItem {
  readonly kind: "blacklist";
  readonly role_name: string;
  readonly tool_name: string;
  readonly source: ToolBlacklistSource;
  // Известен ли tool_name backend'у (`is_known_tool`) — имя не
  // валидируется структурно, опечатку UI показывает через этот флаг.
  readonly known: boolean;
}

export interface AddToolBlacklistItemRequest {
  readonly role_name: string;
  readonly tool_name: string;
}

/**
 * HTTP-ошибка с числовым `status`. Секция не парсит body и не
 * различает коды — `status` несёт только `message` (`"<fn>: HTTP
 * <status>"`), показанный пользователю через `onError` мутации как
 * есть. Зеркало `toolAllowlist.HttpError`.
 */
export class HttpError extends Error {
  public readonly status: number;
  public constructor(status: number, message: string) {
    super(message);
    this.name = "HttpError";
    this.status = status;
  }
}

function buildHeaders(): Record<string, string> {
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
}

export async function fetchToolBlacklist(
  roleName: string,
): Promise<readonly ToolBlacklistItem[]> {
  const query = new URLSearchParams({ role_name: roleName });
  const response = await authedFetch(
    `/api/v1/agent_tool_blacklist?${query.toString()}`,
    { headers: buildHeaders() },
  );
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `fetchToolBlacklist: HTTP ${String(response.status)}`,
    );
  }
  return (await response.json()) as readonly ToolBlacklistItem[];
}

export async function addToolBlacklistItem(
  body: AddToolBlacklistItemRequest,
): Promise<ToolBlacklistItem> {
  const response = await authedFetch("/api/v1/agent_tool_blacklist", {
    method: "POST",
    headers: buildHeaders(),
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `addToolBlacklistItem: HTTP ${String(response.status)}`,
    );
  }
  return (await response.json()) as ToolBlacklistItem;
}

export async function removeToolBlacklistItem(
  roleName: string,
  toolName: string,
): Promise<void> {
  const query = new URLSearchParams({
    role_name: roleName,
    tool_name: toolName,
  });
  const response = await authedFetch(
    `/api/v1/agent_tool_blacklist?${query.toString()}`,
    { method: "DELETE", headers: buildHeaders() },
  );
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `removeToolBlacklistItem: HTTP ${String(response.status)}`,
    );
  }
}
